import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, FastForward, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { api } from '../../lib/api';
import { formatDate, formatDateShort } from '../../lib/dateFormat';

type Schritt = {
  key: string;
  titel: string;
  ok: boolean;
  ergebnis?: unknown;
  fehler?: string;
};

type Nachricht = { kanal: string; an: string; betreff: string | null; art: string | null };

type Tag = {
  tag: string;
  schritte: Schritt[];
  kennzahlen: {
    forderungenCents: number;
    eingaengeCents: number;
    saldoCents: number;
    vertraegeOffen: number;
    kontenGemahnt: number;
    buchungen: Record<string, number>;
  };
  nachrichten: Nachricht[];
  fehler: number;
};

type Lauf = {
  id: number;
  von: string;
  bis: string;
  status: 'pending' | 'running' | 'finished' | 'failed';
  aktuellerTag: string | null;
  tageFertig: number;
  tageGesamt: number;
  fehler: string | null;
  tage: Tag[];
};

type Status = {
  standortId: number | null;
  simuliertBis: string | null;
  naechsterTag: string | null;
  lauf: Lauf | null;
};

const euro = (cents: number) =>
  (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

function plusTage(iso: string, tage: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + tage);
  return d.toISOString().slice(0, 10);
}

/** Kurzfassung eines Schritt-Ergebnisses: nur Zahlen ungleich 0. */
function kurz(ergebnis: unknown): string {
  if (!ergebnis || typeof ergebnis !== 'object') return '';
  const teile: string[] = [];
  const sammeln = (obj: Record<string, unknown>, präfix = '') => {
    Object.entries(obj).forEach(([k, v]) => {
      if (typeof v === 'number' && v !== 0) teile.push(`${präfix}${k}: ${v}`);
      else if (v && typeof v === 'object' && !Array.isArray(v)) {
        sammeln(v as Record<string, unknown>, `${k}.`);
      }
    });
  };
  sammeln(ergebnis as Record<string, unknown>);
  return teile.join(' · ');
}

/**
 * Simulation „bis Datum X“ (docs/simulation-plan.md): spult die E2E-Testschule Tag für
 * Tag vor — Cronjobs und Büro-Routinen mit dem simulierten Datum, Mails gesperrt.
 */
export default function SimulationCard() {
  const [status, setStatus] = useState<Status | null>(null);
  const [bis, setBis] = useState('');
  const [startet, setStartet] = useState(false);
  const [offen, setOffen] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const antwort = await api.get<Status>('/api/admin/e2e-seed/simulation');
      setStatus(antwort);
      return antwort;
    } catch {
      setStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const läuft = status?.lauf?.status === 'pending' || status?.lauf?.status === 'running';

  useEffect(() => {
    if (!läuft) return undefined;
    const timer = window.setInterval(() => {
      void laden();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [läuft, laden]);

  useEffect(() => {
    if (status?.naechsterTag && (!bis || bis < status.naechsterTag)) {
      setBis(plusTage(status.naechsterTag, 29));
    }
  }, [status?.naechsterTag, bis]);

  const daten = useMemo(
    () =>
      (status?.lauf?.tage ?? []).map((t) => ({
        tag: formatDateShort(t.tag),
        Forderungen: t.kennzahlen.forderungenCents / 100,
        Eingänge: t.kennzahlen.eingaengeCents / 100,
        'Offener Saldo': t.kennzahlen.saldoCents / 100,
      })),
    [status?.lauf?.tage],
  );

  if (!status) return null;
  if (!status.standortId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FastForward className="h-4 w-4" /> Simulation
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Erst die Testdaten anlegen — simuliert wird die E2E-Testschule.
        </CardContent>
      </Card>
    );
  }

  const starten = async () => {
    setStartet(true);
    try {
      await api.post('/api/admin/e2e-seed/simulation', { bis });
      toast.success(`Simulation bis ${formatDate(bis)} gestartet`);
      await laden();
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Simulation konnte nicht starten');
    } finally {
      setStartet(false);
    }
  };

  const lauf = status.lauf;
  const fortschritt = lauf ? Math.round((lauf.tageFertig / Math.max(1, lauf.tageGesamt)) * 100) : 0;
  const tageNeuesteZuerst = [...(lauf?.tage ?? [])].reverse();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FastForward className="h-4 w-4" /> Bis Datum simulieren
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Spult die E2E-Testschule Tag für Tag vor: Beiträge, Raten, Termine, Honorare,
          Kündigungen, Belege, Mahnungen, Lastschriftläufe und Überweisungen — mit dem
          simulierten Datum. Mails und SMS gehen nicht raus, sie stehen im Tagesergebnis.
          {status.simuliertBis
            ? ` Simuliert bis ${formatDate(status.simuliertBis)}; der nächste Lauf beginnt am ${formatDate(status.naechsterTag ?? '')}.`
            : ' Noch nicht simuliert.'}
          {' '}Zurück geht es über „Zurücksetzen und neu anlegen“.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Bis einschließlich</span>
            <Input
              type="date"
              value={bis}
              min={status.naechsterTag ?? undefined}
              onChange={(e) => setBis(e.target.value)}
              disabled={läuft}
              className="w-44"
            />
          </label>
          {[
            ['+1 Woche', 6],
            ['+1 Monat', 29],
            ['+3 Monate', 90],
          ].map(([label, tage]) => (
            <Button
              key={label as string}
              type="button"
              variant="outline"
              size="sm"
              disabled={läuft || !status.naechsterTag}
              onClick={() => setBis(plusTage(status.naechsterTag as string, tage as number))}
            >
              {label}
            </Button>
          ))}
          <Button type="button" onClick={starten} disabled={läuft || startet || !bis}>
            {läuft || startet ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Simulieren
          </Button>
        </div>

        {lauf && (
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>
                {formatDate(lauf.von)} – {formatDate(lauf.bis)}
                {lauf.aktuellerTag ? ` · zuletzt ${formatDate(lauf.aktuellerTag)}` : ''}
              </span>
              <span className="text-muted-foreground">
                {lauf.tageFertig}/{lauf.tageGesamt} Tage
                {lauf.status === 'finished' ? ' · fertig' : ''}
                {lauf.status === 'failed' ? ' · abgebrochen' : ''}
              </span>
            </div>
            <Progress value={fortschritt} />
            {lauf.fehler && (
              <p className="flex items-center gap-1 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" /> {lauf.fehler}
              </p>
            )}
          </div>
        )}

        {daten.length > 0 && (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={daten} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="tag" tick={{ fontSize: 11 }} minTickGap={16} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} €`} width={64} />
                <Tooltip formatter={(v: number) => euro(Math.round(v * 100))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Forderungen" fill="hsl(var(--primary))" />
                <Bar dataKey="Eingänge" fill="#5aac86" />
                <Line type="monotone" dataKey="Offener Saldo" stroke="#d97706" dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {tageNeuesteZuerst.length > 0 && (
          <div className="divide-y rounded-md border text-sm">
            {tageNeuesteZuerst.map((t) => {
              const aufgeklappt = offen === t.tag;
              const ereignisse = t.schritte.filter((s) => !s.ok || kurz(s.ergebnis) !== '');
              return (
                <div key={t.tag}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50"
                    onClick={() => setOffen(aufgeklappt ? null : t.tag)}
                  >
                    {aufgeklappt ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span className="w-24 font-medium">{formatDate(t.tag)}</span>
                    <span className="flex-1 text-muted-foreground">
                      {ereignisse.length === 0
                        ? 'nichts passiert'
                        : ereignisse.map((s) => s.titel).join(', ')}
                    </span>
                    {t.nachrichten.length > 0 && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" /> {t.nachrichten.length}
                      </span>
                    )}
                    {t.fehler > 0 && (
                      <span className="flex items-center gap-1 text-destructive">
                        <AlertTriangle className="h-3.5 w-3.5" /> {t.fehler}
                      </span>
                    )}
                    <span className="w-28 text-right tabular-nums">{euro(t.kennzahlen.saldoCents)}</span>
                  </button>
                  {aufgeklappt && (
                    <div className="space-y-2 bg-muted/30 px-10 py-3">
                      <ul className="space-y-1">
                        {t.schritte.map((s) => (
                          <li key={s.key} className={s.ok ? '' : 'text-destructive'}>
                            <span className="font-medium">{s.titel}</span>
                            {': '}
                            {s.ok ? kurz(s.ergebnis) || '—' : s.fehler}
                          </li>
                        ))}
                      </ul>
                      <p className="text-muted-foreground">
                        Forderungen {euro(t.kennzahlen.forderungenCents)} · Eingänge{' '}
                        {euro(t.kennzahlen.eingaengeCents)} · offene Verträge{' '}
                        {t.kennzahlen.vertraegeOffen} · gemahnt {t.kennzahlen.kontenGemahnt}
                      </p>
                      {t.nachrichten.length > 0 && (
                        <ul className="space-y-0.5 text-muted-foreground">
                          {t.nachrichten.map((n, i) => (
                            <li key={i}>
                              {n.kanal === 'sms' ? 'SMS' : 'Mail'} an {n.an}: {n.betreff || '—'}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
